(function () {
  'use strict';

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const STEP = 0.5;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function icon(path) {
    return `<svg viewBox="0 0 24 24" aria-hidden="true">${path}</svg>`;
  }

  function enhanceViewer(modal) {
    if (!modal || modal.dataset.florameViewerReady === '1') return;

    const dialog = modal.querySelector('.product-media-modal__dialog');
    const content = modal.querySelector('.product-media-modal__content');
    if (!dialog || !content) return;

    modal.dataset.florameViewerReady = '1';
    modal.classList.add('florame-product-viewer');

    const toolbar = document.createElement('div');
    toolbar.className = 'florame-product-viewer__toolbar';
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', 'Controlli zoom immagine');
    toolbar.innerHTML = `
      <button type="button" data-florame-viewer="out" aria-label="Riduci immagine">
        ${icon('<path d="M5 12h14"></path>')}
      </button>
      <button type="button" data-florame-viewer="reset" aria-label="Adatta immagine allo schermo">
        <span class="florame-product-viewer__status" aria-live="polite">Adatta</span>
      </button>
      <button type="button" data-florame-viewer="in" aria-label="Ingrandisci immagine">
        ${icon('<path d="M12 5v14M5 12h14"></path>')}
      </button>`;

    const hint = document.createElement('p');
    hint.className = 'florame-product-viewer__hint';
    hint.textContent = 'Tocca o pizzica per ingrandire';

    dialog.appendChild(hint);
    dialog.appendChild(toolbar);

    const zoomOut = toolbar.querySelector('[data-florame-viewer="out"]');
    const resetButton = toolbar.querySelector('[data-florame-viewer="reset"]');
    const zoomIn = toolbar.querySelector('[data-florame-viewer="in"]');
    const status = toolbar.querySelector('.florame-product-viewer__status');

    let image = null;
    let scale = MIN_SCALE;
    let translateX = 0;
    let translateY = 0;
    let pointers = new Map();
    let gesture = null;
    let press = null;

    function activeImage() {
      const active = content.querySelector('img.active[data-media-id], .active img');
      return active && active.tagName === 'IMG' ? active : null;
    }

    function bounds() {
      if (!image) return { x: 0, y: 0 };
      return {
        x: Math.max(0, (image.offsetWidth * (scale - 1)) / 2),
        y: Math.max(0, (image.offsetHeight * (scale - 1)) / 2),
      };
    }

    function constrainTranslation() {
      const limit = bounds();
      translateX = clamp(translateX, -limit.x, limit.x);
      translateY = clamp(translateY, -limit.y, limit.y);
    }

    function applyTransform(animate) {
      if (!image) return;
      constrainTranslation();
      image.classList.toggle('is-florame-zoomed', scale > MIN_SCALE + 0.01);
      image.style.transitionDuration = animate ? '' : '0ms';
      image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
      modal.classList.toggle('is-zoomed', scale > MIN_SCALE + 0.01);
      if (status) status.textContent = scale <= MIN_SCALE + 0.01 ? 'Adatta' : `${Math.round(scale * 100)}%`;
      if (zoomOut) zoomOut.disabled = scale <= MIN_SCALE + 0.01;
      if (zoomIn) zoomIn.disabled = scale >= MAX_SCALE - 0.01;
    }

    function setScale(nextScale, clientX, clientY, animate) {
      if (!image) return;
      const previous = scale;
      const next = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      if (next === previous) return;

      if (Number.isFinite(clientX) && Number.isFinite(clientY) && previous > 0) {
        const rect = image.getBoundingClientRect();
        const offsetX = clientX - (rect.left + rect.width / 2);
        const offsetY = clientY - (rect.top + rect.height / 2);
        const ratio = next / previous;
        translateX -= offsetX * (ratio - 1);
        translateY -= offsetY * (ratio - 1);
      }

      scale = next;
      if (scale <= MIN_SCALE + 0.01) {
        translateX = 0;
        translateY = 0;
      }
      applyTransform(animate !== false);
    }

    function resetViewer(nextImage) {
      if (image && image !== nextImage) {
        image.classList.remove('is-florame-zoomed', 'is-florame-dragging');
        image.style.removeProperty('transform');
        image.style.removeProperty('transition-duration');
      }

      image = nextImage || activeImage();
      scale = MIN_SCALE;
      translateX = 0;
      translateY = 0;
      pointers.clear();
      gesture = null;
      press = null;

      content.scrollTop = 0;
      content.scrollLeft = 0;

      if (!image) return;
      image.loading = 'eager';
      image.draggable = false;
      applyTransform(false);
      requestAnimationFrame(() => applyTransform(true));
    }

    function distance(first, second) {
      return Math.hypot(second.x - first.x, second.y - first.y);
    }

    function midpoint(first, second) {
      return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
    }

    function onPointerDown(event) {
      if (!image || event.target !== image) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      press = { x: event.clientX, y: event.clientY, time: Date.now(), moved: false };
      image.classList.add('is-florame-dragging');
      try { image.setPointerCapture(event.pointerId); } catch (_) {}

      if (pointers.size === 2) {
        const points = Array.from(pointers.values());
        gesture = {
          distance: Math.max(1, distance(points[0], points[1])),
          scale,
          midpoint: midpoint(points[0], points[1]),
        };
      }
      event.preventDefault();
    }

    function onPointerMove(event) {
      if (!image || !pointers.has(event.pointerId)) return;
      const previous = pointers.get(event.pointerId);
      const next = { x: event.clientX, y: event.clientY };
      pointers.set(event.pointerId, next);

      if (press && Math.hypot(next.x - press.x, next.y - press.y) > 5) press.moved = true;

      if (pointers.size >= 2 && gesture) {
        const points = Array.from(pointers.values()).slice(0, 2);
        const nextDistance = Math.max(1, distance(points[0], points[1]));
        const center = midpoint(points[0], points[1]);
        setScale(gesture.scale * (nextDistance / gesture.distance), center.x, center.y, false);
      } else if (scale > MIN_SCALE + 0.01) {
        translateX += next.x - previous.x;
        translateY += next.y - previous.y;
        applyTransform(false);
      }
      event.preventDefault();
    }

    function onPointerEnd(event) {
      if (!image || !pointers.has(event.pointerId)) return;
      event.stopPropagation();
      pointers.delete(event.pointerId);
      image.classList.toggle('is-florame-dragging', pointers.size > 0);

      if (pointers.size < 2) gesture = null;
      if (pointers.size === 0 && press) {
        const elapsed = Date.now() - press.time;
        if (!press.moved && elapsed < 280) {
          if (scale <= MIN_SCALE + 0.01) setScale(2, event.clientX, event.clientY, true);
          else setScale(MIN_SCALE, event.clientX, event.clientY, true);
        } else {
          applyTransform(true);
        }
        press = null;
      }
      event.preventDefault();
    }

    function onWheel(event) {
      if (!image || event.target !== image) return;
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? STEP : -STEP), event.clientX, event.clientY, true);
    }

    content.addEventListener('pointerdown', onPointerDown, { passive: false });
    content.addEventListener('pointermove', onPointerMove, { passive: false });
    content.addEventListener('pointerup', onPointerEnd, { passive: false });
    content.addEventListener('pointercancel', onPointerEnd, { passive: false });
    content.addEventListener('wheel', onWheel, { passive: false });

    // Dawn closes every media modal on a bubbling mouse pointerup. Keep the
    // viewer controls and the image gesture inside the viewer instead.
    toolbar.addEventListener('pointerup', (event) => event.stopPropagation());
    toolbar.addEventListener('click', (event) => event.stopPropagation());
    zoomOut.addEventListener('click', () => setScale(scale - STEP, undefined, undefined, true));
    zoomIn.addEventListener('click', () => setScale(scale + STEP, undefined, undefined, true));
    resetButton.addEventListener('click', () => resetViewer(image));

    dialog.addEventListener('keydown', (event) => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setScale(scale + STEP, undefined, undefined, true);
      } else if (event.key === '-') {
        event.preventDefault();
        setScale(scale - STEP, undefined, undefined, true);
      } else if (event.key === '0') {
        event.preventDefault();
        resetViewer(image);
      }
    });

    const openObserver = new MutationObserver(() => {
      const isOpen = modal.hasAttribute('open');
      document.documentElement.classList.toggle('florame-product-viewer-open', isOpen);
      if (isOpen) {
        requestAnimationFrame(() => resetViewer(activeImage()));
      } else {
        resetViewer(null);
      }
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function init() {
    document.querySelectorAll('product-modal.product-media-modal').forEach(enhanceViewer);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
